import "@arco-design/web-react/es/ConfigProvider/style";
import _ConfigProvider from "@arco-design/web-react/es/ConfigProvider";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import './style/global.less';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { BrowserRouter, Switch, Route, useHistory } from 'react-router-dom';
import request from '@/utils/request';
import store from './store';
import PageLayout from './layout';
import { GlobalContext } from './context';
import Login from './pages/login';
import Register from './pages/register';
import Forgot from './pages/forgot';
import CreateCorp from './pages/createCorp';
import Init from './pages/init';
import Exception500 from './pages/exception/500';
import checkLogin from './utils/checkLogin';
import changeTheme from './utils/changeTheme';
import useStorage from './utils/useStorage';
import { removeAccessToken, removeRefreshToken } from './utils/token';
import { DEFAULT_LANG } from './locale';

/** 雇员信息读取结果：ok 正常 / need_init 企业未初始化 / fail 读取失败 */

function Index() {
  const [lang, setLang] = useStorage('language', DEFAULT_LANG);
  const [theme, setTheme] = useStorage('arco-theme', 'light');
  function getArcoLocale() {
    switch (lang) {
      case 'zh-CN':
        return zhCN;
      case 'en-US':
        return enUS;
      default:
        return zhCN;
    }
  }

  // 退出登录并跳转登录页
  function logoutToLogin() {
    removeAccessToken();
    removeRefreshToken();
    window.location.pathname = '/login';
  }

  // 读取用户的企业雇员信息（含权限），需在初始化数据之前完成
  function fetchEmpProfile() {
    store.dispatch({
      type: 'update-userInfo',
      payload: {
        userLoading: true
      }
    });
    return request.get('/v1/corp/emp/profile').then(res => {
      const {
        code,
        message,
        data
      } = res.data;
      if (code !== 200) {
        _Message.error(message);
        store.dispatch({
          type: 'update-userInfo',
          payload: {
            userInfo: {},
            userLoading: false
          }
        });
        return 'fail';
      }

      // 企业未完成初始化（corp.status < 2）：permissions 为空，只能进独立初始化页
      if (typeof data.corp_status === 'number' && data.corp_status < 2) {
        store.dispatch({
          type: 'update-userInfo',
          payload: {
            userInfo: data,
            userLoading: false
          }
        });
        return 'need_init';
      }

      // 权限数组转为 resource -> actions 映射
      if (data.permissions && Array.isArray(data.permissions)) {
        const permissions = {};
        data.permissions.forEach(item => {
          permissions[item.resource] = item.actions;
        });
        data.permissions = permissions;
      }
      store.dispatch({
        type: 'update-userInfo',
        payload: {
          userInfo: data,
          userLoading: false
        }
      });
      return 'ok';
    }).catch(err => {
      _Message.error(err.message);
      store.dispatch({
        type: 'update-userInfo',
        payload: {
          userInfo: {},
          userLoading: false
        }
      });
      return 'fail';
    });
  }

  // 联合获取初始化数据
  function fetchInitData() {
    return Promise.all([request.get('/v1/public/status/common'), request.get('/v1/public/biz/enums'), request.get('/v1/public/oper/rules'), request.get('/v1/corp/defect/get')]).then(([statusRes, enumRes, operRes, defectRes]) => {
      let allOk = true;

      // 通用状态
      const {
        code: sc,
        message: sm,
        data: sd
      } = statusRes.data;
      if (sc === 200) {
        store.dispatch({
          type: 'update-common-status-map',
          payload: {
            CommonStatusMap: sd
          }
        });
      } else {
        _Message.error(sm);
        allOk = false;
      }

      // 业务枚举
      const {
        code: ec,
        message: em,
        data: ed
      } = enumRes.data;
      if (ec === 200) {
        store.dispatch({
          type: 'update-biz-enum',
          payload: {
            BizStatus: ed.status,
            BizType: ed.type,
            Actions: ed.actions,
            PurchaseType: ed.purchase_type,
            WarehouseType: ed.warehouse_type,
            InboundType: ed.inbound_type,
            OutboundType: ed.outbound_type,
            StockFlowType: ed.stock_flow_type,
            CommonStatus: ed.common_status
          }
        });
      } else {
        _Message.error(em);
        store.dispatch({
          type: 'update-biz-enum',
          payload: {
            BizStatus: [],
            BizType: [],
            Actions: [],
            PurchaseType: [],
            WarehouseType: [],
            InboundType: [],
            OutboundType: [],
            StockFlowType: [],
            CommonStatus: []
          }
        });
        allOk = false;
      }

      // 操作规则
      const {
        code: oc,
        message: om,
        data: od
      } = operRes.data;
      if (oc === 200) {
        store.dispatch({
          type: 'update-oper-rules',
          payload: {
            operRules: od
          }
        });
      } else {
        _Message.error(om);
        store.dispatch({
          type: 'update-oper-rules',
          payload: {
            operRules: []
          }
        });
        allOk = false;
      }

      // 缺陷类型
      const {
        code: dc,
        message: dm,
        data: dd
      } = defectRes.data;
      if (dc === 200) {
        store.dispatch({
          type: 'update-defect-type',
          payload: {
            DefectType: dd
          }
        });
      } else {
        _Message.error(dm);
        store.dispatch({
          type: 'update-defect-type',
          payload: {
            DefectType: []
          }
        });
        allOk = false;
      }
      return allOk;
    }).catch(err => {
      // 用户信息由 fetchEmpProfile 维护，此处不再重置
      _Message.error(err.message);
      return false;
    });
  }
  const history = useHistory();
  useEffect(() => {
    // 路径归一化：去掉尾斜杠，避免 /init/ 与 /init 不匹配导致刷新循环
    const rawPath = window.location.pathname;
    const path = rawPath.endsWith('/') && rawPath.length > 1 ? rawPath.slice(0, -1) : rawPath;
    if (checkLogin()) {
      // 先读取用户的企业雇员信息，成功后再拉取初始化数据
      fetchEmpProfile().then(profile => {
        if (profile === 'fail') {
          // logout and redirect to login page
          logoutToLogin();
          return;
        }

        // 企业未初始化：强制进入独立初始化页（整页刷新，绕过 PageLayout 路由守卫）
        if (profile === 'need_init') {
          if (path !== '/init') {
            window.location.href = '/init';
          }
          return;
        }

        // 已初始化却停留在初始化页：回首页（整页刷新）
        if (path === '/init') {
          window.location.href = '/';
          return;
        }
        fetchInitData().then(ok => {
          if (!ok) {
            // logout and redirect to login page
            logoutToLogin();
            return;
          }
          if (path === '/login') {
            // 已登录但仍在登录页，跳转首页（整页刷新）
            window.location.href = '/';
          }
        });
      });
    } else if (path !== '/login') {
      window.location.href = '/login';
    }
  }, []);
  useEffect(() => {
    changeTheme(theme);
  }, [theme]);
  const contextValue = {
    lang,
    setLang,
    theme,
    setTheme
  };
  return /*#__PURE__*/React.createElement(BrowserRouter, null, /*#__PURE__*/React.createElement(_ConfigProvider, {
    locale: getArcoLocale(),
    componentConfig: {
      Card: {
        bordered: false
      },
      List: {
        bordered: false
      },
      Table: {
        border: false
      }
    }
  }, /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(GlobalContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    path: "/login",
    component: Login
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/register",
    component: Register
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/forgot",
    component: Forgot
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/createCorp",
    component: CreateCorp
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/init",
    component: Init
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/exception/500",
    component: Exception500
  }), /*#__PURE__*/React.createElement(Route, {
    path: "/",
    component: PageLayout
  }))))));
}
const root = createRoot(document.getElementById('root'));
root.render(/*#__PURE__*/React.createElement(Index, null));